import { appManager, AppState } from '../manager';
import {
  arrayToIndexedObject,
  addElementToIndexedArray,
  makeAPIProp,
} from 'services/indexedObject';

import * as types from '../types';
import {
  AddInstrumentsToRunningSessions,
  AddUserToClass,
  AssetEodPreview,
  IndexFundPreview,
  RefreshAssetEod,
  RefreshIndexFund,
  Trainee,
  User,
  UnderlyingAsset,
  ExecutionErrorType,
  TriedAndFailedOrderType,
  PlayerWithDifferences,
  PartialOrderType,
  MarketHoursType,
  AssetClassOptions,
  AssetIntradayPreview,
  ExecutedMoosPartialOrderType,
  LastOpenPricesType,
  LastOpenPricesStateObjectType,
  MarketHistoryCSVType,
  ClientDealingScenarioType,
  ClientDealingScenarioDataType,
  AdminETFDetails,
  AdminETFDetailsTable,
  Preset,
  MacroBackgroundType,
} from '../types';
import RefreshAssetEodModal from '../../../views/AdminDashboard/Data/AssetsTab/RefreshAssetEodModal';
import { notification } from 'antd';
import { Moment } from 'moment';
import MacroBackground from '../../../views/TraineeSession/MacroBackground';
import { result } from 'lodash';

export * from './lsegQuota';
export * from './intradayFetch';

export const fetchCurrencies = appManager.createApi<
  unknown,
  types.Currency[],
  AppState
>('FETCH_CURRENCIES', {
  path: '/currencies/fetch_currencies',
  method: 'GET',
  successReducer(state, result) {
    state.currencies.items = arrayToIndexedObject(result);
  },
});

export const updateCurrency = appManager.createApi<
  types.Currency,
  types.Currency,
  AppState
>('UPDATE_CURRENCY', {
  path: '/currencies/update_currency',
  method: 'POST',
  successReducer(state, result) {
    addElementToIndexedArray(state.currencies.items, result);
  },
});

export const updateFxForwards = appManager.createApi<
  Partial<UnderlyingAsset>,
  UnderlyingAsset,
  AppState
>('UPDATE_FX_FORWARDS', {
  path: '/admin/update_fx_forwards',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    state.underlying_assets.items[result.id].no_of_decimals =
      result.no_of_decimals;
    state.underlying_assets.items[result.id].corresponding_fx_asset =
      result.corresponding_fx_asset;
    // addElementToIndexedArray(state.currencies.items, result);
  },
});

// TODO: THESE SHOULD PROBABLY BE IN THE UNDERLYING ASSETS SLICE ?!

export const updateFXCross = appManager.createApi<
  Partial<types.FXCross> & { id: number },
  types.FXCross,
  AppState
>('UPDATE_FXCROSS', {
  path: '/fx_cross/:id',
  method: 'POST',
  successReducer(state, result) {
    addElementToIndexedArray(state.fx_cross.items, result);
    addElementToIndexedArray(state.underlying_assets.items, result);
  },
});

export const fetchFXCross = appManager.createApi<
  unknown,
  types.FXCross[],
  AppState
>('FETCH_FXCROSS', {
  path: '/fx_cross',
  method: 'GET',
  successReducer(state, result) {
    state.fx_cross.items = arrayToIndexedObject(result);
  },
});

export const createFXCross = appManager.createApi<
  types.CreateFXCross,
  types.FXCross,
  AppState
>('CREATE_FXCROSS', {
  path: '/fx_cross',
  method: 'POST',
  successReducer(state, result) {
    addElementToIndexedArray(state.fx_cross.items, result);
    addElementToIndexedArray(state.underlying_assets.items, result);
  },
});

export const deleteFXCross = appManager.createApi<
  { id: number },
  unknown,
  AppState
>('DELETE_FXCROSS', {
  path: '/fx_cross/:id',
  method: 'DELETE',
  successReducer(state, result, payload) {
    delete state.fx_cross.items[payload.id];
    delete state.underlying_assets.items[payload.id];
  },
});

export const fetchAssets = appManager.createApi<
  // Excludes retired assets
  unknown,
  types.UnderlyingAsset[],
  AppState
>('FETCH_ASSETS', {
  path: '/admin/fetch_assets',
  method: 'GET',
  startReducer(state) {
    state.underlying_assets.waiting = true;
    state.underlying_assets.error = undefined;
  },
  successReducer(state, result) {
    state.underlying_assets.items = arrayToIndexedObject(result);
    state.underlying_assets.fetched = true;
    state.underlying_assets.waiting = false;
  },
  failReducer(state) {
    state.underlying_assets.waiting = false;
    state.underlying_assets.error = 'Assets could not be loaded.';
  },
});

export const fetchAllAssets = appManager.createApi<
  unknown,
  types.UnderlyingAsset[],
  AppState
>('FETCH_ALL_ASSETS', {
  path: '/admin/fetch_all_assets',
  method: 'GET',
  successReducer(state, result) {
    state.underlying_assets.items = arrayToIndexedObject(result);
    state.underlying_assets.fetched = true;
  },
});

export const fetchFxForwards = appManager.createApi<
  unknown,
  Partial<types.UnderlyingAsset>[],
  AppState
>('FETCH_FX_FORWARDS', {
  path: '/underlying_assets/list_fx_forwards',
  method: 'GET',
  successReducer(state, result) {
    // console.log(result);
    state.underlying_assets.items = arrayToIndexedObject(result);
    state.underlying_assets.fetched = true;
  },
});

export const fetchEquities = appManager.createApi<
  unknown,
  types.Equity[],
  AppState
>('FETCH_EQUITIES', {
  path: '/admin/fetch_equities',
  method: 'GET',
  successReducer(state, result) {
    state.equities.items = arrayToIndexedObject(result);
    state.equities.fetched = true;
  },
});

export const updateAdminEquity = appManager.createApi<
  Partial<types.Equity>,
  types.Equity,
  AppState
>('UPDATE_ADMIN_EQUITY', {
  path: '/admin/update_admin_equity',
  method: 'POST',
  startReducer(state, props) {
    const id = props.id;
    if (typeof id !== 'number') {
      return;
    }
    const items = state.equities.items;
    items[id] = { ...items[id], ...props };
  },
  successReducer(state, result) {
    const equities = state.equities.items;
    equities[result.id] = { ...equities[result.id], ...result };
  },
});

export const fetchUnderlyingDeposits = appManager.createApi<
  unknown,
  types.DepositUnderlying[],
  AppState
>('FETCH_DEPOSITS', {
  path: '/admin/fetch_deposits',
  method: 'GET',
  successReducer(state, result) {
    state.underlying_deposits.items = arrayToIndexedObject(result);
  },
});

export const updateDeposits = appManager.createApi<
  Partial<types.DepositUnderlying>,
  types.DepositUnderlying,
  AppState
>('UPDATE_DEPOSITS', {
  path: '/admin/update_deposits',
  method: 'POST',
  startReducer(state, props) {
    const id = props.id;
    if (typeof id !== 'number') {
      return;
    }
    const items = state.equities.items;
    items[id] = { ...items[id], ...props };
  },
  successReducer(state, result) {
    addElementToIndexedArray(state.underlying_assets.items, result);
  },
});

export const updateAsset = appManager.createApi<
  Partial<types.UnderlyingAsset>,
  types.UnderlyingAsset,
  AppState
>('UPDATE_ASSET', {
  path: '/admin/update_asset',
  method: 'POST',
  startReducer(state, props) {
    const id = props.id || 0;
    const items = state.underlying_assets.items;
    items[id] = { ...items[id], ...props };
  },
  successReducer() {
    // addElementToIndexedArray(state.underlying_assets.items, result);
  },
});

export const fetchIndexFunds = appManager.createApi<
  unknown,
  types.IndexFund[],
  AppState
>('FETCH_INDEX_FUNDS', {
  path: '/admin/fetch_index_funds',
  method: 'GET',
  successReducer(state, result) {
    state.index_funds.items = arrayToIndexedObject(result);
  },
});

export const updateIndexFund = appManager.createApi<
  types.IndexFund,
  types.IndexFund,
  AppState
>('UPDATE_INDEX_FUND', {
  path: '/admin/update_index_fund',
  method: 'POST',
  successReducer(state, result) {
    addElementToIndexedArray(state.index_funds.items, result);
  },
});

export const updateETFDetails = appManager.createApi<
  types.AdminETFDetailsTable,
  types.AdminETFDetails,
  AppState
>('UPDATE_ETF_DETAILS', {
  path: '/admin/update_etf_details',
  method: 'POST',
  successReducer(state, result) {
    addElementToIndexedArray(state.etf_details.items, result);
    state.etf_details.items[result.id].investment_theme =
      result.investment_theme;
  },
});

export const fetchNews = appManager.createApi<
  { start_date: string; end_date: string },
  types.News[],
  AppState
>('FETCH_NEWS', {
  path: '/admin/fetch_news',
  method: 'GET',
  startReducer(state) {
    state.news.fetching = true;
  },
  failReducer(state) {
    state.news.fetching = false;
  },
  successReducer(state, result) {
    state.news.items = arrayToIndexedObject(result);
    state.news.fetching = false;
  },
});

export const updateNews = appManager.createApi<
  Partial<types.News>,
  types.News,
  AppState
>('UPDATE_News', {
  path: '/admin/update_news',
  method: 'POST',
  successReducer(state, result) {
    addElementToIndexedArray(state.news.items, result);
  },
});

export const computeVolatility = appManager.createApi(
  'ADMIN_COMPUTE_VOLATILITY',
  {
    path: '/admin/compute_volatility',
    method: 'POST',
    successReducer() {
      /* empty */
    },
  },
);

export const computeBeta = appManager.createApi('ADMIN_COMPUTE_BETA', {
  path: '/admin/compute_beta',
  method: 'POST',
  successReducer() {
    /* empty */
  },
});

export const deleteCorrelationValues = appManager.createApi<
  unknown,
  [number, Record<string, unknown>],
  AppState
>('ADMIN_DELETE_CORRELATION_VALUES', {
  path: '/admin/delete_correlation_values',
  method: 'POST',
  successReducer(state, result) {
    if (result) {
      state.admin.message = result[0] + ' CorrelationValues deleted';
    }
  },
});

appManager.createSocketListener<types.EquityBeta, AppState>(
  'beta_computed',
  (state, result) => {
    state.admin.betas[result.id] = result;
  },
);

appManager.createSocketListener<types.EquityVolatility, AppState>(
  'volatility_computed',
  (state, result) => {
    state.admin.volatilities[result.id] = result;
  },
);

export const retrieveAdminPlayer = appManager.createApi<
  { player_id: number; session_id?: number },
  { player: any; orders: types.Order[] },
  AppState
>('RETRIEVE_ADMIN_PLAYER', {
  path: '/sessions/:session_id/retrieve_player_admin',
  method: 'GET',
  startReducer(state, payload) {
    if (!state.players.items[payload.player_id]) return;
    state.players.items[payload.player_id].fetched_data = true;
  },
  successReducer(state, result) {
    const { player, orders } = result;

    Object.assign(state.players.items[player.id], {
      ...player,
      financial_instruments: arrayToIndexedObject(player.financial_instruments),
      fetched_success: true,
      fetched_data: true,
      retrieved_full: true,
    });

    const newOrders = arrayToIndexedObject(orders);
    state.orders.items = { ...state.orders.items, ...newOrders };
  },
});

export const buyDefaultPortfolio = appManager.createApi<
  { sessions: number[]; assets: types.Group[] },
  unknown,
  AppState
>('BUY_DEFAULT_PORTFOLIOS', {
  method: 'POST',
  path: '/sessions/buy_default_portfolio',
  successReducer() {
    return null;
  },
  failReducer() {
    return null;
  },
});

appManager.createSocketListener<types.InitialBuyStatus, AppState>(
  'buy_initial_portfolio_status',
  (state, result) => {
    state.initialPortfolioStatus[result.id] = result;
  },
);

export const listTrainers = appManager.createApi<
  unknown,
  types.Trainer[],
  AppState
>('LIST_TRAINERS', {
  path: '/trainers',
  method: 'GET',
  startReducer(state) {
    state.trainers.waiting = true;
  },
  successReducer(state, result) {
    result.forEach(trainer => {
      state.trainers.items[trainer.id] = trainer;
    });
    state.trainers.waiting = false;
  },
  failReducer(state) {
    state.trainers.waiting = false;
  },
});

export const setTrainerPassword = appManager.createApi<
  { trainer_id: number; password: string },
  unknown,
  AppState
>('SET_TRAINER_PASSWORD', {
  path: '/trainers/:trainer_id/set_password',
  method: 'POST',
  successReducer() {
    //
  },
});

export const deleteKlass = appManager.createApi<
  { klass_id: number },
  { klass_id: number; trainer_id: number },
  AppState
>('DELETE_KLASS', {
  path: '/klasses/delete_klass',
  method: 'DELETE',
  successReducer(state, result) {
    // console.log(result.klass_id);
    delete state.klasses.items[result.klass_id];
    const klass_index = state.trainers.items[
      result.trainer_id
    ].klasses.findIndex(klass => klass.id === result.klass_id);
    state.trainers.items[result.trainer_id].klasses.splice(klass_index, 1);
  },
});

export const deleteTrainee = appManager.createApi<
  { trainee_id: number },
  { trainee_id: number; klass_id: number },
  AppState
>('DELETE_TRAINEE', {
  path: '/users/delete_trainee',
  method: 'DELETE',
  successReducer(state, result) {
    // console.log(result.trainee_id);
    const klass = state.klasses.items[result.klass_id];
    // console.log(klass);
    // console.log(klass.trainees);
    const traineeIndex = klass.trainees.findIndex(
      trainee => trainee.id === result.trainee_id,
    );
    klass.trainees.splice(traineeIndex, 1);
  },
});

export const setTraineeDevplanAccess = appManager.createApi<
  { trainee_id: number; klass_id: number; can_see_devplan: boolean },
  { trainee_id: number; klass_id: number; can_see_devplan: boolean },
  AppState
>('SET_TRAINEE_DEVPLAN_ACCESS', {
  path: '/users/set_devplan_access',
  method: 'POST',
  successReducer(state, result) {
    // The flag is user-level, so reflect it on the trainee wherever they appear
    // (a trainee is normally in a single class, but this stays correct regardless).
    Object.values(state.klasses.items).forEach(klass => {
      const trainee = klass.trainees?.find(t => t.id === result.trainee_id);
      if (trainee) trainee.can_see_devplan = result.can_see_devplan;
    });
  },
});

export const listMarketHistory = appManager.createApi<
  {
    underlying_asset: number;
    frequency: number;
    timestamp_range: string[];
  },
  types.AdminMarketHistory[],
  AppState
>('LIST_MARKET_HISTORY', {
  path: '/market_history/list_history',
  method: 'POST',
  startReducer(state) {
    state.market_history.waiting = true;
  },
  successReducer(state, result) {
    state.market_history.items = {};
    result.forEach(mh => {
      state.market_history.items[mh.id] = mh;
    });
    state.market_history.waiting = false;
  },
  failReducer(state) {
    state.market_history.waiting = false;
  },
});

export const requestIndexFundPreview = appManager.createApi<
  {
    index_fund_id: number;
  },
  IndexFundPreview,
  AppState
>('PREVIEW_INDEX_FUND', {
  path: '/admin/preview_index_fund_eod',
  method: 'POST',
  successReducer(state, result) {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = result.benchmark_returns;
    // console.log(result);
    state.index_fund_preview = result;
  },
  failReducer(state, result) {
    state.index_fund_preview = { data: [], ric: '' };
  },
});

export const requestAssetEodPreview = appManager.createApi<
  {
    asset_id: number;
  },
  AssetEodPreview,
  AppState
>('PREVIEW_ASSET_EOD', {
  path: '/admin/preview_asset_eod',
  method: 'POST',
  successReducer(state, result) {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = result.benchmark_returns;
    // console.log(result);
    state.asset_eod_preview = result;
  },
  failReducer(state, result) {
    state.asset_eod_preview = {
      data: [],
      ric: '',
      is_yield: false,
      is_afs: false,
      supports_yield_preview: false,
    };
  },
});

export const requestAssetIntradayPreview = appManager.createApi<
  {
    asset_id: number;
    latest_rows?: number;
  },
  AssetIntradayPreview,
  AppState
>('PREVIEW_ASSET_INTRADAY', {
  path: '/admin/preview_asset_intraday',
  method: 'POST',
  successReducer(state, result) {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = result.benchmark_returns;
    // console.log(result);
    state.asset_intraday_preview = result;
  },
  failReducer(state, result) {
    state.asset_intraday_preview = { data: [], ric: '' };
  },
});

export const refreshIndexFundDataFromRefinitiv = appManager.createApi<
  RefreshIndexFund,
  unknown,
  AppState
>('REFRESH_INDEX_FUND_DATA_FROM_REFINITIV', {
  path: '/admin/refresh_index_fund_data_from_refinitiv',
  method: 'POST',
  successReducer(state, result) {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = result.benchmark_returns;
    // console.log(result);
    // state.index_fund_preview = result;
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

export const refreshAssetEodFromRefinitiv = appManager.createApi<
  RefreshAssetEod,
  { requested_asset_ids?: number[]; skipped_asset_ids?: number[] },
  AppState
>('REFRESH_ASSET_EOD_FROM_REFINITIV', {
  path: '/admin/refresh_asset_eod_from_refinitiv',
  method: 'POST',
  successReducer(state, result) {
    if (result?.skipped_asset_ids?.length) {
      notification.warning({
        message: 'EoD refresh completed with skipped assets',
        description: `Skipped asset IDs: ${result.skipped_asset_ids.join(', ')}. Their history was preserved. See Execution Errors for duplicate RIC details.`,
      });
    } else {
      notification.success({ message: 'EoD refresh completed' });
    }
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

export const uploadClientDealingScenario = appManager.createApi<
  FormData,
  unknown,
  AppState
>('UPLOAD_CLIENT_DEALING_SCENARIO', {
  path: '/admin/upload_client_dealing_scenario_csv',
  method: 'POST',
  startReducer(state, result) {
    // pass
  },
  successReducer(state, result) {
    // pass
  },
  failReducer(state, result) {
    // pass
  },
});

export const uploadNews = appManager.createApi<
  { news_file: unknown },
  unknown,
  AppState
>('UPLOAD_NEWS', {
  path: '/admin/upload_news',
  method: 'POST',
  successReducer(state, result) {
    // pass
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

type NewsRephrasingUploadResult = {
  updated_count: number;
  updated: {
    id: number;
    rephrased_headline: string | null;
  }[];
};

export const uploadNewsRephrasing = appManager.createApi<
  { news_rephrasing_file: string },
  NewsRephrasingUploadResult,
  AppState
>('UPLOAD_NEWS_REPHRASING', {
  path: '/admin/upload_news_rephrasing',
  method: 'POST',
  successReducer(state, result) {
    result.updated.forEach(update => {
      const news = state.news.items[update.id];
      if (news) news.rephrased_headline = update.rephrased_headline;
    });
  },
});

export const uploadBulkNewAssets = appManager.createApi<
  { new_assets_file: unknown },
  unknown,
  AppState
>('UPLOAD_BULK_NEW_ASSETS', {
  path: '/admin/upload_bulk_assets',
  method: 'POST',
  successReducer(state, result) {
    // pass
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

export const uploadBulkEodPrices = appManager.createApi<
  { eod_file: unknown },
  unknown,
  AppState
>('UPLOAD_BULK_EOD_PRICES', {
  path: '/admin/upload_bulk_eod_prices',
  method: 'POST',
  successReducer(state, result) {
    // pass
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

export const uploadIndexFund = appManager.createApi<
  { index_fund_file: unknown },
  unknown,
  AppState
>('UPLOAD_INDEX_FUND', {
  path: '/admin/upload_index_fund_history',
  method: 'POST',
  successReducer(state, result) {
    // pass
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

export const addUserToClass = appManager.createApi<
  AddUserToClass,
  {
    trainee: Trainee;
    klass_id: number;
  },
  AppState
>('ADD_USER_TO_CLASS', {
  path: '/klasses/add_new_user_to_klass',
  method: 'POST',
  successReducer(state, result) {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = result.benchmark_returns;
    // console.log(result);
    state.klasses.items[result.klass_id].trainees[result.trainee.id] =
      result.trainee;
    // state.index_fund_preview = result;
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

export const addInstrumentsToRunningSessions = appManager.createApi<
  AddInstrumentsToRunningSessions,
  unknown,
  AppState
>('ADD_INSTRUMENTS_TO_RUNNING_SESSIONS', {
  path: '/admin/add_instruments_to_running_sessions',
  method: 'POST',
  successReducer(state, result) {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = result.benchmark_returns;
    // console.log(result);
    // state.klasses.items[result.klass_id].trainees[result.trainee.id] =
    //   result.trainee;
    // state.index_fund_preview = result;
  },
  failReducer(state, result) {
    // state.index_fund_preview = { data: [], ric: '' };
  },
});

// export const resetPass = appManager.createApi<
//   { new_password: string },
//   unknown,
//   AppState
// >('CHANGE_PASS', {
//   path: '/users/my_change_password',
//   method: 'POST',
//   successReducer(state, result) {
//     // const session = state.sessions.items[state.active_session];
//     // session.benchmark_preview = result.benchmark_returns;
//     // console.log(result);
//     // state.klasses.items[result.klass_id].trainees[result.trainee.id] =
//     //   result.trainee;
//     // state.index_fund_preview = result;
//   },
//   failReducer(state, result) {
//     // state.index_fund_preview = { data: [], ric: '' };
//   },
// });

export const clearIndexFundPreview = appManager.createLocalEvent(
  'clear_index_fund_preview',
  state => {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = undefined;
    state.index_fund_preview = undefined;
  },
);

export const clearAssetEodPreview = appManager.createLocalEvent(
  'clear_asset_eod_preview',
  state => {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = undefined;
    state.asset_eod_preview = undefined;
  },
);

export const clearAssetIntradayPreview = appManager.createLocalEvent(
  'clear_asset_intraday_preview',
  state => {
    // const session = state.sessions.items[state.active_session];
    // session.benchmark_preview = undefined;
    state.asset_intraday_preview = undefined;
  },
);

export const retrieveExecutionErrors = appManager.createApi<
  unknown,
  { errors: ExecutionErrorType[] },
  AppState
>('RETRIEVE_EXECUTION_ERRORS', {
  path: '/errors/get_execution_errors',
  method: 'GET',
  startReducer(state, payload) {
    state.errors_tab.execution_errors = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.errors_tab.execution_errors = makeAPIProp(result.errors);
    state.errors_tab.execution_errors.fetched = true;
    state.errors_tab.execution_errors.fetching = false;
  },
  failReducer(state) {
    state.errors_tab.execution_errors.fetching = false;
    state.errors_tab.execution_errors.fetched = false;
  },
});

export const markErrorAsSeen = appManager.createApi<
  { id: number },
  unknown,
  AppState
>('MARK_ERROR_AS_SEEN', {
  path: '/errors/mark_error_as_seen',
  method: 'POST',
  startReducer(state, payload) {
    //
  },
  successReducer(state, result, payload) {
    state.errors_tab.execution_errors.items[payload.id].seen = true;
  },
  failReducer(state) {
    //
  },
});

export const retrieveTriedAndFailedOrders = appManager.createApi<
  unknown,
  { err_orders: TriedAndFailedOrderType[] },
  AppState
>('RETRIEVE_TRIED_AND_FAILED_ORDERS', {
  path: '/errors/get_pending_orders_with_one_or_more_execution_tries',
  method: 'GET',
  startReducer(state, payload) {
    state.errors_tab.tried_and_failed_orders = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.errors_tab.tried_and_failed_orders = makeAPIProp(result.err_orders);
    state.errors_tab.tried_and_failed_orders.fetched = true;
    state.errors_tab.tried_and_failed_orders.fetching = false;
  },
  failReducer(state) {
    state.errors_tab.tried_and_failed_orders.fetching = false;
    state.errors_tab.tried_and_failed_orders.fetched = false;
  },
});

export const retrievePlayersWithDiffNumbers = appManager.createApi<
  { difference: number },
  { diff_players: PlayerWithDifferences[] },
  AppState
>('RETRIEVE_PLAYERS_WITH_DIFF_NUMBERS', {
  path: '/errors/get_players_with_differences',
  method: 'POST',
  startReducer(state, payload) {
    state.errors_tab.players_with_differences = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.errors_tab.players_with_differences = makeAPIProp(
      result.diff_players,
    );
    state.errors_tab.players_with_differences.fetched = true;
    state.errors_tab.players_with_differences.fetching = false;
  },
  failReducer(state) {
    state.errors_tab.players_with_differences.fetching = false;
    state.errors_tab.players_with_differences.fetched = false;
  },
});

export const retrievePendingMoos = appManager.createApi<
  unknown,
  { pending_moos: PartialOrderType[] },
  AppState
>('RETRIEVE_PENDING_MOOS', {
  path: '/errors/get_pending_moos',
  method: 'GET',
  startReducer(state, payload) {
    state.errors_tab.pending_moos = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.errors_tab.pending_moos = makeAPIProp(result.pending_moos);
    state.errors_tab.pending_moos.fetched = true;
    state.errors_tab.pending_moos.fetching = false;
  },
  failReducer(state) {
    state.errors_tab.pending_moos.fetching = false;
    state.errors_tab.pending_moos.fetched = false;
  },
});

export const retrieveExecutedMoos = appManager.createApi<
  unknown,
  { moos: ExecutedMoosPartialOrderType[] },
  AppState
>('RETRIEVE_EXECUTED_MOOS', {
  path: '/errors/get_moos_executed_today',
  method: 'GET',
  startReducer(state, payload) {
    state.errors_tab.moos_executed_today = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.errors_tab.moos_executed_today = makeAPIProp(result.moos);
    state.errors_tab.moos_executed_today.fetched = true;
    state.errors_tab.moos_executed_today.fetching = false;
  },
  failReducer(state) {
    state.errors_tab.moos_executed_today.fetching = false;
    state.errors_tab.moos_executed_today.fetched = false;
  },
});

export const retrievePendingSlTps = appManager.createApi<
  unknown,
  { sltps: PartialOrderType[] },
  AppState
>('RETRIEVE_PENDING_SLTPS', {
  path: '/errors/get_all_pending_sl_tp',
  method: 'GET',
  startReducer(state, payload) {
    state.errors_tab.pending_sl_tps = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.errors_tab.pending_sl_tps = makeAPIProp(result.sltps);
    state.errors_tab.pending_sl_tps.fetched = true;
    state.errors_tab.pending_sl_tps.fetching = false;
  },
  failReducer(state) {
    state.errors_tab.pending_sl_tps.fetching = false;
    state.errors_tab.pending_sl_tps.fetched = false;
  },
});

export const fetchMarketHours = appManager.createApi<
  unknown,
  { market_hours: MarketHoursType[] },
  AppState
>('FETCH_MARKET_HOURS', {
  path: '/market_hours/get_market_hours',
  method: 'GET',
  startReducer(state, payload) {
    state.market_hours = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    // console.log('market hours result: ', result);
    state.market_hours = makeAPIProp(result.market_hours);
    state.market_hours.fetched = true;
    state.market_hours.fetching = false;
  },
  failReducer(state) {
    state.market_hours.fetching = false;
    state.market_hours.fetched = false;
  },
});

export const fetchETFDetails = appManager.createApi<
  unknown,
  { details: AdminETFDetails[] },
  AppState
>('FETCH_ETF_DETAILS', {
  path: '/underlying_assets/get_all_etf_details',
  method: 'GET',
  startReducer(state, payload) {
    state.etf_details = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    // console.log('market hours result: ', result);
    state.etf_details.items = arrayToIndexedObject(result.details);
    // console.log(result);

    state.etf_details.fetched = true;
    state.etf_details.fetching = false;
  },
  failReducer(state) {
    state.etf_details.fetching = false;
    state.etf_details.fetched = false;
  },
});

export const fetchClientDealingScenarios = appManager.createApi<
  unknown,
  { scenarios: ClientDealingScenarioType[] },
  AppState
>('FETCH_CLIENT_DEALING_SCENARIOS', {
  path: '/client_money/fetch_client_dealing_scenarios',
  method: 'GET',
  startReducer(state, payload) {
    state.client_dealing_scenarios = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    // console.log('market hours result: ', result);
    state.client_dealing_scenarios.items = arrayToIndexedObject(
      result.scenarios,
    );
    state.client_dealing_scenarios.fetched = true;
    state.client_dealing_scenarios.fetching = false;
  },
  failReducer(state) {
    state.client_dealing_scenarios.fetching = false;
    state.client_dealing_scenarios.fetched = false;
  },
});

export const fetchClientDealingScenarioData = appManager.createApi<
  { scenario_id: number },
  { data: ClientDealingScenarioDataType[] },
  AppState
>('FETCH_CLIENT_DEALING_SCENARIO_DATA', {
  path: '/admin/fetch_client_dealing_scenario_data',
  method: 'POST',
  startReducer(state, payload) {
    //
  },
  successReducer(state, result) {
    // console.log('market hours result: ', result);
    state.downloaded_scenario = result.data;
  },
  failReducer(state) {
    //
  },
});

export const createMarketHours = appManager.createApi<
  Partial<MarketHoursType>,
  MarketHoursType,
  AppState
>('CREATE_MARKET_HOURS', {
  path: '/market_hours/create_market_hours',
  method: 'POST',
  // startReducer(state, payload) {},
  successReducer(state, result) {
    const items = state.market_hours.items;
    items[result.id] = result;
  },
});

export const updateMarketHours = appManager.createApi<
  { market_hours: { [key: number]: MarketHoursType } },
  // types.AssetClassOptions,
  types.MarketHoursType[],
  AppState
>('UPDATE_MARKET_HOURS', {
  path: '/market_hours/edit_market_hours',
  method: 'POST',
  successReducer(state, result) {
    // response received on socket at updated_asset_class_options
    state.market_hours = makeAPIProp(result);
  },
});

appManager.createSocketListener<{ failed_item: string; err: string }, AppState>(
  // TODO: doesn't work
  'market_hours_update_err',
  (state, result) => {
    notification.error({
      message: `Update failed for ${result.failed_item}`,
      description: result.err,
    });
  },
);

appManager.createSocketListener<ExecutionErrorType, AppState>(
  'execution_error_created',
  (state, result) => {
    addElementToIndexedArray(state.errors_tab.execution_errors.items, result);
    state.errors_tab.execution_errors.fetched = true;
    state.errors_tab.execution_errors.fetching = false;

    notification.error({
      message: 'New system error',
      description: result.text,
      duration: 1,
    });
  },
);

appManager.createSocketListener<
  { utc_now: string; geography: string },
  AppState
>('executing_moo_moc_admin_notification', (state, result) => {
  console.log('result: ', result);
  notification.success({
    message: `EXECUTING ${result.geography}`,
    description: `UTC now: ${result.utc_now}`,
    duration: 20,
  });
});

appManager.createSocketListener<
  {
    session_id: number;
    summary: types.BenchmarkCoverageSummary;
  },
  AppState
>('benchmark_coverage_recompute_completed', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.eligible_benchmark_options_waiting = false;
    session.benchmark_coverage_summary_waiting = false;
    session.benchmark_coverage_summary = result.summary;
    session.benchmark_coverage_diagnostics = undefined;
    session.benchmark_coverage_diagnostics_waiting = false;
  }

  notification.success({
    message: `Benchmark coverage recomputed for session ${result.session_id}.`,
  });
});

appManager.createSocketListener<
  {
    session_id: number;
    error: string;
  },
  AppState
>('benchmark_coverage_recompute_failed', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.eligible_benchmark_options_waiting = false;
    session.benchmark_coverage_summary_waiting = false;
    session.benchmark_coverage_diagnostics_waiting = false;
  }

  notification.error({
    message: `Benchmark coverage recompute failed for session ${result.session_id}`,
    description: result.error,
    duration: 0,
  });
});

appManager.createSocketListener<
  {
    session_id: number;
    unsupported_mode: boolean;
    blocked: boolean;
    updated: number;
    created: number;
    skipped: number;
    uncovered: { [date: string]: string[] };
    missing_snapshots: { [date: string]: number[] };
    var05_deferred: boolean;
  },
  AppState
>('benchmark_metrics_recompute_completed', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.benchmark_metrics_recompute_waiting = false;
  }
  if (result.blocked) {
    notification.warning({
      message: `Benchmark metrics recompute blocked for session ${result.session_id}`,
      description:
        'Missing benchmark prices or player history snapshots need attention before metrics can be rewritten.',
    });
  } else if (result.unsupported_mode) {
    notification.warning({
      message: `Benchmark metrics recompute skipped for session ${result.session_id}`,
      description: 'Only composition benchmarks can be recomputed.',
    });
  } else {
    notification.success({
      message: `Benchmark metrics recomputed for session ${result.session_id} (${result.updated} updated, ${result.created} created).`,
    });
  }
});

appManager.createSocketListener<
  {
    session_id: number;
    error: string;
  },
  AppState
>('benchmark_metrics_recompute_failed', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.benchmark_metrics_recompute_waiting = false;
  }
  notification.error({
    message: `Benchmark metrics recompute failed for session ${result.session_id}`,
    description: result.error,
    duration: 0,
  });
});

appManager.createSocketListener<
  {
    session_id: number;
    err: string;
  },
  AppState
>('analysis_recompute_completed', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.analysis_recompute_waiting = false;
  }
  if (result.err) {
    notification.error({
      message: `Analysis recompute failed for session ${result.session_id}`,
      description: result.err,
      duration: 0,
    });
  }
});

appManager.createSocketListener<
  {
    session_id: number;
    err: string;
  },
  AppState
>('pnl_recompute_completed', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.pnl_recompute_waiting = false;
  }
  if (result.err) {
    notification.error({
      message: `PnL distributions recompute failed for session ${result.session_id}`,
      description: result.err,
      duration: 0,
    });
  }
});

export const fetchLastOpenPrices = appManager.createApi<
  unknown,
  LastOpenPricesStateObjectType,
  AppState
>('FETCH_LAST_OPEN_PRICES', {
  path: '/market_hours/get_first_open_prices',
  method: 'GET',
  startReducer(state, payload) {
    state.last_open_prices = {
      items: {},
      header: [],
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.last_open_prices.items = arrayToIndexedObject(result.rows);
    state.last_open_prices.header = result.header;
    state.last_open_prices.fetched = true;
    state.last_open_prices.fetching = false;
  },
  failReducer(state) {
    state.last_open_prices.fetching = false;
    state.last_open_prices.fetched = false;
  },
});

export const fetchLastClosePrices = appManager.createApi<
  unknown,
  LastOpenPricesStateObjectType,
  AppState
>('FETCH_LAST_CLOSE_PRICES', {
  path: '/market_hours/get_first_close_prices',
  method: 'GET',
  startReducer(state, payload) {
    state.last_close_prices = {
      items: {},
      header: [],
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.last_close_prices.items = arrayToIndexedObject(result.rows);
    state.last_close_prices.header = result.header;
    state.last_close_prices.fetched = true;
    state.last_close_prices.fetching = false;
  },
  failReducer(state) {
    state.last_close_prices.fetching = false;
    state.last_close_prices.fetched = false;
  },
});

export const fetchDownloadMarketHistoryForCsv = appManager.createApi<
  { asset_id: number },
  { rows: MarketHistoryCSVType[] },
  AppState
>('FETCH_MARKET_HISTORY_CSV_DOWNLOAD', {
  path: '/market_hours/get_market_history_csv_download',
  method: 'POST',
  startReducer(state, payload) {
    state.market_history_download_csv = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.market_history_download_csv.items = arrayToIndexedObject(result.rows);
    state.market_history_download_csv.fetched = true;
    state.market_history_download_csv.fetching = false;
  },
  failReducer(state) {
    state.market_history_download_csv.fetching = false;
    state.market_history_download_csv.fetched = false;
  },
});

export const fetchMissingEodPrices = appManager.createApi<
  {
    preset_id: number;
    start_date: string;
    end_date: string;
    preset_name: string;
    automatic_eod: boolean;
    fetch_b_days: boolean;
    frequency: number | undefined;
  },
  {
    type: string;
    geography: string;
    id: number;
    name: string;
    ric: string;
    date: string;
  }[],
  // unknown,
  AppState
>('FETCH_MISSING_PRICES', {
  path: '/admin/get_missing_prices',
  method: 'POST',
  startReducer(state, payload) {
    // state.market_history_download_csv = {
    //   items: {},
    //   fetching: true,
    //   fetched: false,
    // };
  },
  successReducer(state, result, payload) {
    // console.log(result);
    const downloadCsv = (rows: any[], filename = 'missing_prices.csv') => {
      if (!rows || rows.length === 0) return;

      // Extract header row from object keys
      const header = Object.keys(rows[0]).join(',');
      // Map rows to CSV lines
      const csvRows = rows.map(r =>
        Object.values(r)
          .map(v => `"${String(v).replace(/"/g, '""')}"`) // escape quotes
          .join(','),
      );

      const csvString = [header, ...csvRows].join('\n');

      // Create blob and download
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    const now = new Date().toISOString().split('T')[0];
    let filename = '';
    if (payload.fetch_b_days) {
      filename = `missing_prices_preset_${payload.preset_name}_business_days_from_${payload.start_date}_to_${payload.end_date}_ran_on_${now}.csv`;
      if (payload.automatic_eod) {
        filename = `missing_prices_automatic_eod_assets_business_days_from_${payload.start_date}_to_${payload.end_date}_ran_on_${now}.csv`;
      }
    } else {
      filename = `missing_prices_preset_${payload.preset_name}_session_dates_from_${payload.start_date}_to_${payload.end_date}_frequency_${payload.frequency}_ran_on_${now}.csv`;
      if (payload.automatic_eod) {
        filename = `missing_prices_automatic_eod_assets_session_dates_from_${payload.start_date}_to_${payload.end_date}_frequency_${payload.frequency}_ran_on_${now}.csv`;
      }
    }
    downloadCsv(result, filename);
  },
  failReducer(state, error) {
    // state.market_history_download_csv.fetching = false;
    // state.market_history_download_csv.fetched = false;
    console.log(error);
    notification.error({ message: error.message });
  },
});

export const downloadPresetReturns = appManager.createApi<
  {
    preset_id: number;
    start_date: string;
    end_date: string;
    preset_name: string;
    get_per_period_returns: boolean;
    frequency: number;
    samples: number | null;
  },
  {
    type: string;
    geography: string;
    id: number;
    name: string;
    ric: string;
    date: string;
  }[],
  AppState
>('DOWNLOAD_PRESET_RETURNS', {
  path: '/admin/download_preset_returns',
  method: 'POST',
  startReducer(state, payload) {
    // state.market_history_download_csv = {
    //   items: {},
    //   fetching: true,
    //   fetched: false,
    // };
  },
  successReducer(state, result, payload) {
    const downloadCsv = (rows: any[], filename = 'missing_prices.csv') => {
      if (!rows || rows.length === 0) return;

      // Extract header row from object keys
      const header = Object.keys(rows[0]).join(',');
      // Map rows to CSV lines
      const csvRows = rows.map(r =>
        Object.values(r)
          .map(v => `"${String(v).replace(/"/g, '""')}"`) // escape quotes
          .join(','),
      );

      const csvString = [header, ...csvRows].join('\n');

      // Create blob and download
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    const now = new Date().toISOString().split('T')[0];
    let filename = '';
    if (payload.get_per_period_returns) {
      filename = `all_periods_returns_for_preset_${payload.preset_name}_from_${payload.start_date}_to_${payload.end_date}_frequency_${payload.frequency}.csv`;
    } else {
      filename = `sampled_returns_for_preset_${payload.preset_name}_from_${payload.start_date}_to_${payload.end_date}_frequency_${payload.frequency}.csv`;
    }
    downloadCsv(result, filename);
  },
  failReducer(state, error) {
    // state.market_history_download_csv.fetching = false;
    // state.market_history_download_csv.fetched = false;
    console.log(error);
    notification.error({ message: error.message });
  },
});

export const duplicatePreset = appManager.createApi<
  {
    preset_id: number;
    new_name: string;
  },
  Preset,
  // unknown,
  AppState
>('DUPLICATE_PRESET', {
  path: '/admin/duplicate_preset',
  method: 'POST',
  startReducer(state, payload) {
    // ..
  },
  successReducer(state, result, payload) {
    addElementToIndexedArray(state.presets.items, result);
  },
  failReducer(state, error) {
    console.log(error);
    notification.error({ message: error.message });
  },
});

export const deletePreset = appManager.createApi<
  {
    preset_id: number;
  },
  Preset,
  // unknown,
  AppState
>('DELETE_PRESET', {
  path: '/admin/delete_preset',
  method: 'POST',
  startReducer(state, payload) {
    // ..
  },
  successReducer(state, result, payload) {
    delete state.presets.items[payload.preset_id];
  },
  failReducer(state, error) {
    console.log(error);
    notification.error({ message: error.message });
  },
});

export const getMacroBackgrounds = appManager.createApi<
  // {
  //   preset_id: number;
  // },
  unknown,
  MacroBackgroundType[],
  // unknown,
  AppState
>('GET_MACRO_BACKGROUNDS', {
  path: '/admin/get_macro_backgrounds',
  method: 'POST',
  startReducer(state, payload) {
    state.macro_backgrounds.waiting = true;
  },
  successReducer(state, result, payload) {
    state.macro_backgrounds.items = arrayToIndexedObject(result);
    state.macro_backgrounds.waiting = false;
  },
  failReducer(state, error) {
    state.macro_backgrounds.waiting = false;
  },
});

export const updateMacroBackgroundAdmin = appManager.createApi<
  { id: number; name?: string; body?: string },
  MacroBackgroundType,
  AppState
>('UPDATE_MACRO_BACKGROUND_ADMIN', {
  path: '/admin/update_macro_background_admin',
  method: 'POST',
  startReducer(state, payload) {
    state.macro_backgrounds.waiting = true;
  },
  successReducer(state, result, payload) {
    state.macro_backgrounds.items[payload.id] = result;
    state.macro_backgrounds.waiting = false;
  },
  failReducer(state, error) {
    state.macro_backgrounds.waiting = false;
  },
});

export const createMacroBackgroundAdmin = appManager.createApi<
  { name: string; body: string },
  MacroBackgroundType,
  AppState
>('CREATE_MACRO_BACKGROUND_ADMIN', {
  path: '/admin/create_macro_background_admin',
  method: 'POST',
  startReducer(state, payload) {
    state.macro_backgrounds.waiting = true;
  },
  successReducer(state, result, payload) {
    addElementToIndexedArray(state.macro_backgrounds.items, result);
    state.macro_backgrounds.waiting = false;
  },
  failReducer(state, error) {
    state.macro_backgrounds.waiting = false;
  },
});

export const deleteMacroBackground = appManager.createApi<
  { id: number },
  unknown,
  AppState
>('DELETE_MACRO_BACKGROUND_ADMIN', {
  path: '/admin/delete_macro_background_admin',
  method: 'POST',
  startReducer(state, payload) {
    state.macro_backgrounds.waiting = true;
  },
  successReducer(state, result, payload) {
    delete state.macro_backgrounds.items[payload.id];
    state.macro_backgrounds.waiting = false;
  },
  failReducer(state, error) {
    state.macro_backgrounds.waiting = false;
  },
});

export const duplicateMacroBackground = appManager.createApi<
  { id: number },
  MacroBackgroundType,
  AppState
>('DUPLICATE_MACRO_BACKGROUND_ADMIN', {
  path: '/admin/duplicate_macro_background_admin',
  method: 'POST',
  startReducer(state, payload) {
    state.macro_backgrounds.waiting = true;
  },
  successReducer(state, result, payload) {
    addElementToIndexedArray(state.macro_backgrounds.items, result);
    state.macro_backgrounds.waiting = false;
  },
  failReducer(state, error) {
    state.macro_backgrounds.waiting = false;
  },
});

export const updatePrompt = appManager.createApi<
  { prompt_id: number; name: string; text: string; type?: types.PromptKind },
  types.PromptType,
  AppState
>('UPDATE_PROMPT', {
  method: 'POST',
  path: '/prompts/update_prompt',
  startReducer(state, payload) {
    if (payload.type === 'group') {
      state.group_prompts.waiting = true;
      return;
    }
    state.prompts.waiting = true;
  },
  successReducer(state, result) {
    delete state.prompts.items[result.id];
    delete state.group_prompts.items[result.id];
    if (result.type === 'group') {
      state.group_prompts.items[result.id] = result;
      state.group_prompts.waiting = false;
      state.prompts.waiting = false;
      return;
    }
    state.prompts.items[result.id] = result;
    state.prompts.waiting = false;
    state.group_prompts.waiting = false;
  },
  failReducer(state, error, payload) {
    if (payload.type === 'group') {
      state.group_prompts.waiting = false;
      return;
    }
    state.prompts.waiting = false;
  },
});

export const fetchAdminAISessions = appManager.createApi<
  { q?: string },
  types.AdminAISessionOption[],
  AppState
>('FETCH_ADMIN_AI_SESSIONS', {
  method: 'GET',
  path: '/sessions/admin_ai_lookup',
  startReducer(state) {
    state.admin_ai_sessions.waiting = true;
  },
  successReducer(state, result) {
    state.admin_ai_sessions.items = arrayToIndexedObject(result);
    state.admin_ai_sessions.waiting = false;
  },
  failReducer(state) {
    state.admin_ai_sessions.waiting = false;
  },
});

export const fetchAdminAIPlayers = appManager.createApi<
  { session_id: number },
  types.AdminAIPlayerOption[],
  AppState
>('FETCH_ADMIN_AI_PLAYERS', {
  method: 'GET',
  path: '/sessions/:session_id/fetch_ai_players',
  startReducer(state) {
    state.admin_ai_players.waiting = true;
    state.admin_ai_players.items = {};
  },
  successReducer(state, result) {
    state.admin_ai_players.items = arrayToIndexedObject(result);
    state.admin_ai_players.waiting = false;
  },
  failReducer(state) {
    state.admin_ai_players.waiting = false;
  },
});

export const fetchAdminAISessionContext = appManager.createApi<
  { session_id: number },
  types.AdminAISessionContextType,
  AppState
>('FETCH_ADMIN_AI_SESSION_CONTEXT', {
  method: 'GET',
  path: '/prompts/preview_session_context',
  startReducer(state, payload) {
    state.admin_ai_session_context.item = null;
    state.admin_ai_session_context.waiting = true;
    state.admin_ai_session_context.requested_session_id = payload.session_id;
    state.admin_ai_session_context.error = undefined;
  },
  successReducer(state, result, payload) {
    if (
      state.admin_ai_session_context.requested_session_id !== payload.session_id
    ) {
      return;
    }
    state.admin_ai_session_context.item = result;
    state.admin_ai_session_context.waiting = false;
  },
  failReducer(state, error, payload) {
    if (
      state.admin_ai_session_context.requested_session_id !== payload.session_id
    ) {
      return;
    }
    state.admin_ai_session_context.item = null;
    state.admin_ai_session_context.waiting = false;
    state.admin_ai_session_context.error = error;
  },
});

appManager.createSocketListener<{ x: string; y: number }[][], AppState>(
  'segments_computed',
  (state, result) => {
    state.eod_price_segments.items = result;
  },
);
